import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import type { Message } from '../../api/messages'
import useStationStore from '../../store/station'
import { formatMessageTimestamp, resolveEpochMs } from '../../utils/time'
import PlatformIcon from '../icons/PlatformIcon'

export interface MessageBubbleProps {
  msg: Message
  isOwn?: boolean
  metaLabel?: string
  registerVisibility?: (el: HTMLElement, id: number) => void
  onToggleFlag?: (message: Message, next: boolean) => void
  onToggleOnAir?: (message: Message, next: boolean) => void
}

const formatPlatformLabel = (platform: string): string => {
  if (!platform) {
    return 'Unknown source'
  }
  const lower = platform.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  ({ msg, isOwn = false, metaLabel, registerVisibility, onToggleFlag, onToggleOnAir }, ref) => {
    const theme = useTheme()
    const isUnread = !msg.is_read
    const localRef = React.useRef<HTMLDivElement>(null)

    React.useImperativeHandle(ref, () => localRef.current as HTMLDivElement | null, [])

    React.useEffect(() => {
      if (!localRef.current || msg.is_read || !registerVisibility) {
        return
      }

      if (typeof window === 'undefined') {
        registerVisibility(localRef.current, msg.id)
        return
      }

      const handle = window.requestAnimationFrame(() => {
        if (localRef.current) {
          registerVisibility(localRef.current, msg.id)
        }
      })

      return () => {
        if (typeof window !== 'undefined') {
          window.cancelAnimationFrame(handle)
        }
      }
    }, [msg.id, msg.is_read, registerVisibility])

    const stationTimezone = useStationStore((state) => state.activeStation?.timezone)
    const receivedEpochMs = React.useMemo(
      () =>
        resolveEpochMs({
          epochSeconds: msg.received_at_epoch ?? undefined,
          isoString: msg.received_at_iso_utc ?? msg.received_at ?? null,
        }),
      [msg.received_at, msg.received_at_epoch, msg.received_at_iso_utc],
    )

    const timestamp = React.useMemo(() => {
      if (metaLabel) {
        return metaLabel
      }
      return formatMessageTimestamp(receivedEpochMs, stationTimezone)
    }, [metaLabel, receivedEpochMs, stationTimezone])

    const platformLabel = React.useMemo(() => formatPlatformLabel(msg.platform), [msg.platform])

    const senderLabel = React.useMemo(() => {
      if (isOwn) {
        if (msg.sent_by?.display_name && msg.sent_by.display_name.trim().length > 0) {
          return msg.sent_by.display_name
        }
        return 'You'
      }

      if (msg.identity?.display_name && msg.identity.display_name.trim().length > 0) {
        return msg.identity.display_name
      }

      if (msg.contact?.name && msg.contact.name.trim().length > 0) {
        return msg.contact.name
      }

      return 'Listener'
    }, [isOwn, msg.contact?.name, msg.identity?.display_name, msg.sent_by?.display_name])

    const statusChip = React.useMemo(() => {
      if (!isOwn) {
        return null
      }

      const status = msg.transport_status ?? 'sent'
      const label = status.charAt(0).toUpperCase() + status.slice(1)

      let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'success'

      if (status === 'queued') {
        color = 'warning'
      } else if (status === 'failed') {
        color = 'error'
      }

      const chip = (
        <Chip
          label={label}
          size="small"
          color={color}
          variant={status === 'sent' ? 'outlined' : 'filled'}
          sx={{ fontSize: 10, height: 20 }}
        />
      )

      if (status === 'failed' && msg.transport_error) {
        return (
          <Tooltip title={msg.transport_error} placement="top">
            <span>{chip}</span>
          </Tooltip>
        )
      }

      return chip
    }, [isOwn, msg.transport_status, msg.transport_error])

    const bubbleBg = React.useMemo(() => {
      if (isOwn) {
        return alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12)
      }
      if (isUnread) {
        return alpha(theme.palette.action.selected, theme.palette.mode === 'dark' ? 0.45 : 0.35)
      }
      return alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.2 : 0.12)
    }, [isOwn, isUnread, theme])

    const borderColor = React.useMemo(() => {
      if (isUnread) {
        return alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.5 : 0.3)
      }
      return alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.6 : 0.3)
    }, [isUnread, theme])

    const accentColor = React.useMemo(
      () => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.6 : 0.45),
      [theme],
    )

    const classes = React.useMemo(() => {
      const next = ['rpa-msg-bubble']
      if (isUnread) {
        next.push('unread')
      }
      if (isOwn) {
        next.push('rpa-msg-bubble--own')
      }
      return next.join(' ')
    }, [isOwn, isUnread])

    const statusChips = React.useMemo(() => {
      const elements: React.ReactNode[] = []

      if (msg.is_on_air) {
        const chip = (
          <Chip
            label="On-Air"
            size="small"
            color="secondary"
            onClick={(event) => {
              event.stopPropagation()
              onToggleOnAir?.(msg, false)
            }}
            sx={{ fontSize: 10, height: 22 }}
          />
        )

        elements.push(
          <Tooltip key="on-air" title="On-Air — click to clear" placement="top">
            <span>{chip}</span>
          </Tooltip>,
        )
      }

      if (msg.is_flagged) {
        const chip = (
          <Chip
            label="Flagged"
            size="small"
            color="warning"
            onClick={(event) => {
              event.stopPropagation()
              onToggleFlag?.(msg, false)
            }}
            sx={{ fontSize: 10, height: 22 }}
          />
        )

        elements.push(
          <Tooltip key="flagged" title="Flagged — click to clear" placement="top">
            <span>{chip}</span>
          </Tooltip>,
        )
      }

      return elements
    }, [msg, onToggleFlag, onToggleOnAir])

    const autoresponderMeta = React.useMemo(() => {
      if (!msg.is_autoresponder) {
        return null
      }

      const ruleName = msg.auto_rule_name?.trim() ?? ''
      const chip = (
        <Chip
          label="Auto"
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontSize: 10, height: 20 }}
        />
      )

      const content = (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: 200 }}>
          {chip}
          {ruleName ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {ruleName}
            </Typography>
          ) : null}
        </Stack>
      )

      if (!ruleName) {
        return content
      }

      return (
        <Tooltip title={`Autoresponder: ${ruleName}`} placement="top">
          <span>{content}</span>
        </Tooltip>
      )
    }, [msg.auto_rule_name, msg.is_autoresponder])

    return (
      <Box
        ref={localRef}
        className={classes}
        data-msgid={msg.id}
        sx={{
          position: 'relative',
          borderRadius: 1.5,
          padding: isUnread ? '8px 12px 8px 14px' : '8px 12px',
          marginY: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          alignSelf: isOwn ? 'flex-end' : 'flex-start',
          backgroundColor: bubbleBg,
          border: '1px solid',
          borderColor,
          boxShadow: isUnread ? `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}` : 'none',
          '&::before': isUnread
            ? {
                content: '""',
                position: 'absolute',
                inset: '6px auto 6px 6px',
                width: 3,
                borderRadius: 1.5,
                backgroundColor: accentColor,
              }
            : undefined,
        }}
      >
        <Box
          className="rpa-msg-meta"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: 12,
            lineHeight: 1.2,
            opacity: 0.8,
            color: 'text.secondary',
            flexWrap: 'wrap',
          }}
        >
          <PlatformIcon platform={msg.platform} fontSize="inherit" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {platformLabel}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: isOwn ? 600 : 500 }}
          >
            {senderLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {timestamp}
          </Typography>
          {statusChip}
          {autoresponderMeta}
          {statusChips.length ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {statusChips}
            </Stack>
          ) : null}
        </Box>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            userSelect: 'text',
          }}
        >
          {msg.content && msg.content.trim().length ? msg.content : '(no content)'}
        </Typography>
      </Box>
    )
  },
)

MessageBubble.displayName = 'MessageBubble'

export default MessageBubble
